import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { NgIfContext } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  Breakpoint,
  BreakpointState,
  LocalStorageService,
  MediaQueryService,
  SecurityService,
  slideInOutAnimation,
  UtilityService,
  ContainerSize,
  DetailedQuote,
  QuoteChartSize,
  QuoteIssueType,
  QuoteTemplate,
  QuoteTimeRange,
  QuoteTimeSeries,
  QuoteChartConfigService,
  QuoteChartSettings,
  QuoteFrequencyParams,
  QuoteRestService,
  QuoteComponent,
  ChartTypeSelector,
  FrequencySelector,
  QuoteConstants,
  TimeRangeSelector,
  DetailedQuoteTab,
  MarketTypeHelper
} from '@tdwb/core';

@Component({
  selector: 'td-wb-markets-detailed-quote',
  templateUrl: 'detailed-quote.component.html',
  styleUrls: ['detailed-quote.component.scss'],
  animations: [slideInOutAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DetailedQuoteComponent extends QuoteComponent implements OnInit, OnDestroy {
  @Input() viewSize: QuoteChartSize;
  @Input() selectedTab: DetailedQuoteTab;
  @Input() hideAddToWatchlistButton: boolean;
  @Input() hideInterlistExchangeForOTC?: boolean = false;
  @ViewChild('issueType', { static: true }) private templateRefIssueType: TemplateRef<unknown>;

  analystRatingEnabled: boolean = false;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  readonly DetailedQuoteTab: typeof DetailedQuoteTab = DetailedQuoteTab;

  private customizedChartEnabled: boolean;
  private readonly change: Subject<void> = new Subject<void>();
  private breakpointState: BreakpointState;
  private containerSize: ContainerSize = null;

  constructor(translateService: TranslateService,
              quoteChartConfigService: QuoteChartConfigService,
              quoteRestService: QuoteRestService,
              securityService: SecurityService,
              changeDetectorRef: ChangeDetectorRef,
              private mediaQueryService: MediaQueryService,
              private localStorageService: LocalStorageService) {
    super(translateService, quoteChartConfigService, quoteRestService, securityService, changeDetectorRef);
  }

  ngOnInit(): void {
    this.analystRatingEnabled = this.securityService.hasPermission('HOME_PAGE_ANALYST_RATINGS');
    if (this.customizedChartEnabled === undefined) {
      this.customizedChartEnabled = this.securityService.hasPermission('QUOTE_DETAIL_CUSTOMIZED_CHART');
    }
    if (this.customizedChartEnabled === true) {
      this.displayTimeSeries.customizedChartEnabled = true;
      this.displayTimeSeries.types = QuoteConstants.CHART_TYPE_SELECTOR;
    }
    this.mediaQueryService.observe(Breakpoint.SM)
      .pipe(takeUntil(this.destroy))
      .subscribe((breakpointState: BreakpointState): void => {
        this.breakpointState = breakpointState;
        this.changeDetectorRef.markForCheck();
      });
  }

  containerSizeChange(containerSize: ContainerSize): void {
    this.containerSize = containerSize;
    this.changeDetectorRef.markForCheck();
  }

  isLoading(): boolean {
    return this.containerSize === null || this.loading;
  }

  /**
   * Show inter-listed exchanges
   */
  showInterlistedExchangeQuote(): void {
    const tickerSymbol = this.quote.interlistedExchanges[0].symbol;
    const country = this.quote.interlistedExchanges[0].country;

    this.interlistedExchangeSymbol = UtilityService.deepClone(this.symbol);
    this.interlistedExchangeSymbol.tickerSymbol = tickerSymbol;
    this.interlistedExchangeSymbol.market = MarketTypeHelper.fromString(country);
    this.refreshQuote();
  }

  isMutualFund(): boolean {
    return this.quote.issueType === QuoteIssueType.MF;
  }

  /**
   * return true if  Morning star rating displayed for current security
   */
  showStarRating(): boolean {
    return this.quote.issueType === QuoteIssueType.MF
        || this.quote.issueType === QuoteIssueType.ETF;
  }

  showAnalystRating(): boolean {
    if (this.quote !== undefined && this.quote.consensus !== undefined && typeof this.quote.consensus === 'object') {
      return this.analystRatingEnabled === true
        && this.quote.issueType === QuoteIssueType.STOCK
        && this.quote.consensus.overallConsensus !== undefined;
    }
    return this.analystRatingEnabled === true
      && this.quote.issueType === QuoteIssueType.STOCK
      && this.quote.consensus !== undefined;
  }

  showWarningMessage(): boolean {
    return this.quote.issueType !== QuoteIssueType.MF
        && this.quote.issueType !== QuoteIssueType.OPTION;
  }

  /**
   * return true if buy sell enabled for current security
   */
  showBuySell(): boolean {
    return this.inactivateBuySell !== true
        && this.tradeInfo !== undefined
        && this.quote.issueType !== QuoteIssueType.INDEX;
  }

  /**
   * Returns chart size based on the current window size
   */
  getChartSize(): QuoteChartSize {
    if ((this.containerSize !== undefined && this.containerSize === ContainerSize.SM) || this.viewSize === 'compact') {
      return 'compact';
    }
    return 'full';
  }

  /**
   * Handle tab change
   */
  switchTab(tabId: DetailedQuoteTab): void {
    this.selectedTab = tabId;
    let storedObject = this.localStorageService.getItem(QuoteConstants.LOCAL_STORAGE_KEY);
    if (storedObject === null || storedObject === undefined) {
      storedObject = {};
    }

    const key = this.getLocalStorageQuoteType(this.quote.issueType);
    storedObject[key] = this.selectedTab;
    this.localStorageService.setItem(QuoteConstants.LOCAL_STORAGE_KEY, storedObject);
  }

  onFrequencyChange(): void {
    this.saveChartSettings();
    this.refreshQuote();
  }

  // Chart type dropdown change callback.
  // Stores selection for later and , since we are only changing chart type and do not need new data,
  // uses internal seriesData and immediately invokes requestTimeSeriesSuccess sending ohlcSeries as if was a response
  onTypeChange(): void {
    this.saveChartSettings();
    const timeRangeType = this.displayTimeSeries.selectTimeRange.type;
    if (timeRangeType === QuoteTimeRange.DAILY) {
      this.requestTimeSeriesSuccess({ ohlcSeries: this.seriesData }, undefined);
    } else {
      const chartSeries = this.displayTimeSeries.selectTimeRange.chartSeries;
      const updateSeries = this.dailyTimeSeriesFrequency(chartSeries);
      this.requestTimeSeriesSuccess({ ohlcSeries: this.seriesData }, updateSeries);
    }
  }

  /**
   * Handles on tab change event
   * Creates new chartConfig object so that child components
   * can detect changes in chartConfig
   *
   * Refresh chartConfig based on selected tab
   */
  onTabChange(tabId: number): void {
    if (this.customizedChartEnabled) {
      this.displayTimeSeries.value = tabId;
      this.onTimeRangeChange();
    } else {
      super.onTabChange(tabId);
    }
  }

  /**
   * Handles time range change
   */
  onTimeRangeChange(): void {
    const selectTimeRange = this.displayTimeSeries.selectTimeRange;
    this.assignmentFrequencies(selectTimeRange, undefined);
    this.saveChartSettings();
    this.refreshQuote();
  }

  isSmallScreen(): boolean {
    return this.breakpointState?.matches;
  }

  hasModPermission(): boolean {
    return this.securityService.hasPermission('MOD');
  }

  /**
   * Refresh quote. To be used externally for now
   */
  refresh(): void {
    this.refreshQuote();
  }

  castToNgIfContextTemplateRef(templateRef: unknown): TemplateRef<NgIfContext<boolean>> {
    return templateRef as TemplateRef<NgIfContext<boolean>>;
  }

  /**
   * Gets template based on issue type
   */
  protected getIssueTypeTemplate(): TemplateRef<unknown> | undefined {
    return this.templateRefIssueType;
  }

  /**
   * Called on quote retrieval success
   *
   * Updates template and quote object
   * Emits new quote object
   *
   */
  protected requestQuoteSuccess(quote: DetailedQuote): DetailedQuote {
    // TODO: MERGE IT WITH BASE CLASS METHOD
    if (this.customizedChartEnabled !== undefined && quote?.defaultTimeSeries !== undefined && this.customizedChartEnabled !== null || this.customizedChartEnabled === true) {
      quote.defaultTimeSeries.closeSeries = [];
    }

    if (this.selectedTab === undefined && quote !== undefined) {
      this.selectedTab = this.getInitialTab(quote.issueType);
    }
    return super.requestQuoteSuccess(quote);
  }

  /**
   * Update chart config
   * Called on rest service success
   */
  protected configureChart(quote: DetailedQuote): void {
    if (!this.customizedChartEnabled) {
      super.configureChart(quote);
      return;
    }

    let selectTimeRange;
    const selectChartSettings = this.quoteChartConfigService.getChartSettings(quote.issueType);

    this.template = QuoteTemplate.ISSUE_TYPE;
    this.displayTimeSeries.tabs = quote.issueType === QuoteIssueType.MF ?
      QuoteConstants.TABS_MF : QuoteConstants.TABS_DEFAULT;

    this.chartConfig = this.quoteChartConfigService.createTimeSeriesStockChartConfig(quote);

    this.displayTimeSeries.timeRanges = quote.issueType === QuoteIssueType.MF ?
      QuoteConstants.DEFAULT_TIME_RANGE_SELECTOR.mutualFund : QuoteConstants.DEFAULT_TIME_RANGE_SELECTOR.others;

    if (selectChartSettings !== undefined && selectChartSettings.selectType !== undefined) {
      this.displayTimeSeries.selectType = QuoteConstants.CHART_TYPE_SELECTOR
        .find((chartTypeSelector: ChartTypeSelector): boolean => chartTypeSelector.type === selectChartSettings.selectType.type);
    } else {
      this.displayTimeSeries.selectType = QuoteConstants.CHART_TYPE_SELECTOR[0];
    }

    if (selectChartSettings !== undefined) {
      selectTimeRange = this.displayTimeSeries.timeRanges
        .find( (timeRange: TimeRangeSelector): boolean =>  timeRange.type === selectChartSettings.selectTimeRange.type);
    }

    if (selectTimeRange === undefined ) {
      this.displayTimeSeries.selectTimeRange = this.displayTimeSeries.timeRanges[0];
      this.displayTimeSeries.selectFrequency = QuoteConstants.DEFAULT_FREQUENCY_SELECTOR.DAILY[0];

      this.assignmentFrequencies(this.displayTimeSeries.selectTimeRange);
    } else {
      this.displayTimeSeries.selectTimeRange = selectTimeRange;
      this.assignmentFrequencies(this.displayTimeSeries.selectTimeRange, selectChartSettings.selectFrequency);
    }
    this.quoteChartConfigService.addChartVolume(this.chartConfig);
    this.displayTimeSeriesEnableFrequency(this.displayTimeSeries.selectTimeRange);
  }

  /**
   * Gets initial tab from local storage
   */
  private getInitialTab(type: QuoteIssueType): DetailedQuoteTab {
    const storedObject = this.localStorageService.getItem(QuoteConstants.LOCAL_STORAGE_KEY);
    const consumerKey = this.hideAddToWatchlistButton !== undefined ?
      QuoteConstants.WATCHLIST : QuoteConstants.HOLDINGS;
    const initialTabByConsumer = consumerKey === QuoteConstants.HOLDINGS ?
      QuoteConstants.SUMMARY_TAB : QuoteConstants.MORE_DETAILS_TAB;
    type = this.getLocalStorageQuoteType(type);
    if (storedObject !== null && storedObject[type] !== undefined) {
      return storedObject[type];
    } else {
      return initialTabByConsumer as DetailedQuoteTab;
    }
  }

  /**
   * get local storage type based on issue type
   */
  private getLocalStorageQuoteType(type: QuoteIssueType): QuoteIssueType {
    return type === QuoteIssueType.ETF ? QuoteIssueType.STOCK : type;
  }

  /**
   * Save chart settings
   *
   */
  private saveChartSettings(): void {
    const rememberSettings: QuoteChartSettings = {
      selectTimeRange: this.displayTimeSeries.selectTimeRange,
      selectFrequency: this.displayTimeSeries.selectFrequency,
      selectType: this.displayTimeSeries.selectType
    };
    this.quoteChartConfigService.setChartSettings(rememberSettings, this.quote.issueType);
  }

  /**
   * Assignment frequencies
   */
  private assignmentFrequencies(timeRange: TimeRangeSelector, frequency: FrequencySelector = undefined): void {
    if (timeRange.frequencies === undefined) {
      return;
    }

    this.displayTimeSeries.frequencies = QuoteConstants.DEFAULT_FREQUENCY_SELECTOR[timeRange.frequencies];
    if (frequency !== undefined) {
      this.displayTimeSeries.selectFrequency = frequency;
    }

    this.displayTimeSeries.selectFrequency = this.displayTimeSeries.frequencies
      .find((freq: FrequencySelector): boolean =>  freq.timePeriod === this.displayTimeSeries.selectFrequency.timePeriod &&
        freq.interval === this.displayTimeSeries.selectFrequency.interval);

    if (this.displayTimeSeries.selectFrequency === undefined) {
      if (timeRange.chartSeries === QuoteTimeRange.THREE_YEARS || timeRange.chartSeries === QuoteTimeRange.FIVE_YEARS ||
          timeRange.chartSeries === QuoteTimeRange.TEN_YEARS || timeRange.chartSeries === QuoteTimeRange.TWENTY_YEARS ) {
        this.displayTimeSeries.selectFrequency = this.displayTimeSeries.frequencies[1];
      } else {
        this.displayTimeSeries.selectFrequency = this.displayTimeSeries.frequencies[0];
      }
    }
  }

  /**
   * Enable frequency for chart
   */
  private displayTimeSeriesEnableFrequency(timeRange: TimeRangeSelector): void {
    if (timeRange.chartSeries !== undefined) {
      const request: QuoteFrequencyParams = {
        symbol: this.quote.tickerSymbolForTimeSeries,
        market: this.getSymbolMarket(),
        timeRange: timeRange.type,
        timePeriod: this.displayTimeSeries.selectFrequency.timePeriod,
        interval: this.displayTimeSeries.selectFrequency.interval
      };

      const updateChart = this.dailyTimeSeriesFrequency(timeRange.chartSeries);
      if (updateChart !== undefined) {
        this.getTimeSeriesEnabledFrequency(request, updateChart);
      } else {
        if (timeRange.chartSeries === QuoteTimeRange.INTRADAY) {
          this.displayIntraDayTimeSeriesEnableFrequency(request);
        } else {
          this.displayDefaultTimeSeries();
        }
      }
    }
  }

  /**
   * Gets time series data
   */
  private getTimeSeriesEnabledFrequency(request: QuoteFrequencyParams, updateChartSeries: unknown): void {
    this.change.next();
    this.quoteRestService.getTimeSeriesEnableFrequency(request)
      .pipe(takeUntil(merge(this.change, this.destroy)))
      .subscribe((timeSeries: QuoteTimeSeries): void => {
        this.requestTimeSeriesSuccess(timeSeries, updateChartSeries);
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * Gets intra daytime series data
   */
  private displayIntraDayTimeSeriesEnableFrequency(request: QuoteFrequencyParams): void {
    this.change.next();
    this.quoteRestService.getTimeSeriesEnableFrequency(request)
      .pipe(takeUntil(merge(this.change, this.destroy)))
      .subscribe((timeSeries: QuoteTimeSeries): void => {
        this.requestTimeSeriesSuccess(timeSeries, undefined);
        this.changeDetectorRef.markForCheck();
      });
  }
}
